import { FC } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import type { CourseCertificate } from "../../api/compliance";

interface ValidityPeriodProps {
  courseCertificate: CourseCertificate;
}

/**
 * Renders a course's validity-period months and, when the value is a position-specific override
 * (CREW-538), an info tooltip explaining it overrides the course default. Shared by the main
 * Certificate row and the grouped/refresher sub-rows so the hint reads consistently everywhere.
 */
const ValidityPeriod: FC<ValidityPeriodProps> = ({ courseCertificate }) => {
  const { t } = useTranslation();
  const { validPeriod, courseDefaultValidPeriod } = courseCertificate;
  // The value is a position-specific override when the backend sent the original course default
  // and it differs from the effective validPeriod.
  const isPositionOverride = courseDefaultValidPeriod != null && courseDefaultValidPeriod !== validPeriod;

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {validPeriod ? t("certificate.months", { count: validPeriod }) : ""}
      {isPositionOverride && (
        <Tooltip
          title={
            validPeriod
              ? t("validity_period_position_override_hint", {
                  months: validPeriod,
                  courseMonths: courseDefaultValidPeriod ?? 0,
                })
              : t("validity_period_position_override_hint_never_expires", {
                  courseMonths: courseDefaultValidPeriod ?? 0,
                })
          }
        >
          <InfoOutlinedIcon color="action" fontSize="inherit" sx={{ cursor: "help", flexShrink: 0 }} />
        </Tooltip>
      )}
    </Box>
  );
};

export default ValidityPeriod;
